import { useState, useEffect } from "react";
import { API, useAuth , authFetch, useEditMode } from "@/App";
import { toast } from "sonner";
import { UserPlus, Shield, Trash2, Mail, Lock, User, Settings2, Edit } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const ROLES = [
  { value: "SUPER_ADMIN", label: "Super Admin" },
  { value: "SALES_ADMIN", label: "Sales Admin" },
  { value: "ACCOUNTS_ADMIN", label: "Accounts Admin" },
  { value: "FACTORY_ADMIN", label: "Factory Admin" },
  { value: "AREA_MANAGER", label: "Area Manager" },
  { value: "SALES_REP", label: "Sales Rep / Salesman" },
  { value: "FIELD_EXECUTIVE", label: "Field Executive" },
];

const ROLE_COLORS = {
  SUPER_ADMIN: "bg-red-100 text-red-700",
  SALES_ADMIN: "bg-blue-100 text-blue-700",
  ACCOUNTS_ADMIN: "bg-emerald-100 text-emerald-700",
  FACTORY_ADMIN: "bg-amber-100 text-amber-700",
  AREA_MANAGER: "bg-violet-100 text-violet-700",
  SALES_REP: "bg-sky-100 text-sky-700",
  FIELD_EXECUTIVE: "bg-slate-100 text-slate-700",
};

// Master list — kept in sync with /app/backend/scripts/grant_super_admin_full_permissions.py
const MODULE_OPTIONS = [
  // Sales
  { value: "sales.dealers", label: "Dealers", group: "Sales" },
  { value: "sales.products", label: "Products", group: "Sales" },
  { value: "sales.orders", label: "Orders", group: "Sales" },
  { value: "sales.collections", label: "Collections", group: "Sales" },
  { value: "sales.schemes", label: "Schemes", group: "Sales" },
  // Operations / HR
  { value: "operations.employees", label: "Employees", group: "Operations" },
  { value: "operations.tasks", label: "Tasks", group: "Operations" },
  { value: "operations.expenses", label: "Expenses", group: "Operations" },
  { value: "hr.attendance", label: "Attendance", group: "Operations" },
  { value: "hr.leaves", label: "Leaves", group: "Operations" },
  { value: "hr.grievances", label: "Grievances", group: "Operations" },
  // Finance
  { value: "finance.collections", label: "Finance Collections", group: "Finance" },
  { value: "finance.expenses", label: "Finance Expenses", group: "Finance" },
  // Field Force
  { value: "field_force.beats", label: "Beats", group: "Field Force" },
  { value: "field_force.routes", label: "Routes", group: "Field Force" },
  { value: "field_force.checkins", label: "Check-ins", group: "Field Force" },
  { value: "field_force.tracking", label: "Live Tracking", group: "Field Force" },
  // Factory
  { value: "factory.production", label: "Production", group: "Factory" },
  { value: "factory.dispatch", label: "Dispatch", group: "Factory" },
  { value: "factory.raw_materials", label: "Raw Materials", group: "Factory" },
  { value: "factory.qc", label: "Quality Control", group: "Factory" },
  { value: "factory.equipment", label: "Equipment", group: "Factory" },
  { value: "factory.energy", label: "Energy", group: "Factory" },
  { value: "factory.safety", label: "Safety", group: "Factory" },
  // Intelligence / Reports
  { value: "intelligence.analytics", label: "Analytics", group: "Intelligence" },
  { value: "intelligence.sales_analytics", label: "Sales Analytics", group: "Intelligence" },
  { value: "intelligence.leaderboard", label: "Leaderboard", group: "Intelligence" },
  { value: "intelligence.reports", label: "Reports", group: "Intelligence" },
  // Admin
  { value: "admin.users", label: "User Management", group: "Admin" },
  { value: "admin.roles", label: "Roles", group: "Admin" },
  { value: "admin.config", label: "System Config", group: "Admin" },
  { value: "admin.audit_logs", label: "Audit Logs", group: "Admin" },
];

const MODULE_GROUPS = ["Sales", "Operations", "Finance", "Field Force", "Factory", "Intelligence", "Admin"];

const FACTORY_SECTIONS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "production", label: "Production Log" },
  { id: "raw-materials", label: "Raw Materials" },
  { id: "finished-goods", label: "Finished Goods" },
  { id: "dispatch", label: "Dispatch Queue" },
  { id: "qc", label: "Quality Control" },
  { id: "safety", label: "Safety" },
  { id: "equipment", label: "Equipment" },
  { id: "energy", label: "Energy" },
  { id: "material-entries", label: "Material Entry Log" },
  { id: "daily-reports", label: "Daily Reports" },
  { id: "expenses", label: "Factory Expenses" },
  { id: "manpower", label: "HR / Manpower" },
  { id: "forecast", label: "AI Forecast" },
  { id: "reports", label: "Reports" },
];

const UserManagementPage = () => {
  const { user: currentUser } = useAuth();
  const canEdit = useEditMode();
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    email: "", name: "", password: "", role: "SALES_REP",
    region_id: "", linked_employee_id: "",
    mobile_login_enabled: true, module_permissions: [],
  });
  const [creating, setCreating] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permModules, setPermModules] = useState([]);
  const [savingPerm, setSavingPerm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "", password: "", region_id: "", factory_visible_sections: [], mobile_login_enabled: true, linked_employee_id: "" });

  const fetchUsers = async () => {
    try {
      const res = await authFetch(`${API}/admin/users`, {  });
      const data = await res.json();
      if (data.success) setUsers(data.data);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await authFetch(`${API}/employees?status=ACTIVE&per_page=500`);
      const data = await res.json();
      if (data.success) setEmployees(data.data || []);
    } catch { /* ignore — employees list is optional */ }
  };

  useEffect(() => { fetchUsers(); fetchEmployees(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { ...form };
      if (!payload.region_id) delete payload.region_id;
      if (!payload.linked_employee_id) delete payload.linked_employee_id;
      const res = await authFetch(`${API}/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`User ${form.email} created — they can ${form.mobile_login_enabled ? "log into both web and mobile app" : "log into web only"}`);
        setForm({ email: "", name: "", password: "", role: "SALES_REP", region_id: "", linked_employee_id: "", mobile_login_enabled: true, module_permissions: [] });
        setShowCreate(false);
        fetchUsers();
        fetchEmployees();
      } else {
        toast.error(data.detail || "Failed to create user");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setCreating(false);
    }
  };

  const toggleCreateModule = (mod) => {
    setForm((prev) => ({
      ...prev,
      module_permissions: prev.module_permissions.includes(mod)
        ? prev.module_permissions.filter((m) => m !== mod)
        : [...prev.module_permissions, mod],
    }));
  };

  const selectAllForCreate = () => {
    setForm((prev) => ({ ...prev, module_permissions: MODULE_OPTIONS.map((m) => m.value) }));
  };

  const clearAllForCreate = () => {
    setForm((prev) => ({ ...prev, module_permissions: [] }));
  };

  const handleDeactivate = async (userId) => {
    if (!confirm("Deactivate this user?")) return;
    try {
      const res = await authFetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("User deactivated");
        fetchUsers();
      } else {
        toast.error(data.detail || "Failed to deactivate user");
      }
    } catch (err) {
      toast.error("Network error");
    }
  };

  const openPermissions = (u) => {
    setPermUser(u);
    setPermModules(u.module_permissions || []);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      name: u.name || "", role: u.role || "", password: "",
      region_id: u.region_id || "",
      factory_visible_sections: u.factory_visible_sections || [],
      mobile_login_enabled: u.mobile_login_enabled !== false,
      linked_employee_id: u.linked_employee_id || "",
    });
  };

  const handleEditSave = async () => {
    if (!editUser) return;
    try {
      const body = {
        name: editForm.name, role: editForm.role,
        region_id: editForm.region_id || null,
        mobile_login_enabled: editForm.mobile_login_enabled,
        linked_employee_id: editForm.linked_employee_id || null,
      };
      if (editForm.password) body.password = editForm.password;
      if (editForm.role === "FACTORY_ADMIN") {
        body.factory_visible_sections = editForm.factory_visible_sections;
      }
      const res = await authFetch(`${API}/admin/users/${editUser.user_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      if (res.ok) { toast.success("User updated"); setEditUser(null); fetchUsers(); fetchEmployees(); }
      else { const d = await res.json(); toast.error(d.detail || "Failed"); }
    } catch { toast.error("Network error"); }
  };

  const toggleFactorySection = (sectionId) => {
    setEditForm(prev => ({
      ...prev,
      factory_visible_sections: prev.factory_visible_sections.includes(sectionId)
        ? prev.factory_visible_sections.filter(s => s !== sectionId)
        : [...prev.factory_visible_sections, sectionId]
    }));
  };

  const toggleAllFactorySections = () => {
    setEditForm(prev => ({
      ...prev,
      factory_visible_sections: prev.factory_visible_sections.length === FACTORY_SECTIONS.length ? [] : FACTORY_SECTIONS.map(s => s.id)
    }));
  };

  const toggleModule = (mod) => {
    setPermModules((prev) =>
      prev.includes(mod) ? prev.filter((m) => m !== mod) : [...prev, mod]
    );
  };

  const savePermissions = async () => {
    if (!permUser) return;
    setSavingPerm(true);
    try {
      const res = await authFetch(`${API}/admin/users/${permUser.user_id}/permissions`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ module_permissions: permModules }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("Permissions updated");
        setPermUser(null);
        fetchUsers();
      } else {
        toast.error(data.detail || "Failed to update permissions");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setSavingPerm(false);
    }
  };

  const isSuperAdmin = currentUser?.role === "SUPER_ADMIN";

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">User Management</h1>
          <p className="text-slate-500 text-sm mt-1">Create logins for web + mobile app · grant per-module access · link to an employee record</p>
        </div>
        {isSuperAdmin && (
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <button
                className="flex items-center gap-2 px-4 py-2.5 bg-[#8B1A1A] text-white rounded-lg hover:bg-[#6B1515] text-sm font-semibold transition-colors shadow-sm"
                data-testid="create-user-btn"
              >
                <UserPlus className="w-4 h-4" /> Add User
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="create-user-dialog">
              <DialogHeader>
                <DialogTitle className="font-['Sora']">Add New User</DialogTitle>
                <DialogDescription>Create a login that works on both web and mobile app. Optionally link to an employee record and grant module access.</DialogDescription>
              </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2" data-testid="create-user-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-700">Full Name</label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="pl-10" required data-testid="user-name-input" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Email (used as login ID)</label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="user@avicement.com" className="pl-10" required data-testid="user-email-input" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Password</label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" className="pl-10" required minLength={6} data-testid="user-password-input" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Role</label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Region (optional)</label>
                  <Select value={form.region_id || "ALL"} onValueChange={(v) => setForm({ ...form, region_id: v === "ALL" ? "" : v })}>
                    <SelectTrigger className="mt-1" data-testid="user-region-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Regions</SelectItem>
                      <SelectItem value="GJ">Gujarat</SelectItem>
                      <SelectItem value="RJ">Rajasthan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Link to Employee (optional)</label>
                  <Select value={form.linked_employee_id || "NONE"} onValueChange={(v) => setForm({ ...form, linked_employee_id: v === "NONE" ? "" : v })}>
                    <SelectTrigger className="mt-1" data-testid="link-employee-select"><SelectValue placeholder="Pick an employee" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">— None —</SelectItem>
                      {employees.filter((e) => !e.linked_user_id).map((e) => (
                        <SelectItem key={e.employee_id} value={e.employee_id}>
                          {e.full_name} ({e.employee_code || e.employee_id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <label className="flex items-center gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/10 rounded-lg border border-emerald-200 cursor-pointer">
                <input type="checkbox" checked={form.mobile_login_enabled} onChange={(e) => setForm({ ...form, mobile_login_enabled: e.target.checked })} className="accent-emerald-600 w-4 h-4" data-testid="mobile-login-toggle" />
                <span className="text-sm text-slate-800">
                  <span className="font-semibold">Allow mobile app login</span> — user can log in to the Pravaah mobile app with this email + password
                </span>
              </label>

              <div className="border rounded-lg p-3 bg-slate-50 dark:bg-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Module access · {form.module_permissions.length} of {MODULE_OPTIONS.length} selected
                  </p>
                  <div className="flex gap-2">
                    <button type="button" onClick={selectAllForCreate} className="text-xs text-[#8B1A1A] hover:underline" data-testid="perm-select-all">Select all</button>
                    <span className="text-slate-300">·</span>
                    <button type="button" onClick={clearAllForCreate} className="text-xs text-slate-500 hover:underline" data-testid="perm-clear-all">Clear</button>
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 mb-3">SUPER_ADMIN gets full access automatically — these checkboxes are for narrower roles.</p>
                <div className="max-h-72 overflow-y-auto space-y-3">
                  {MODULE_GROUPS.map((group) => (
                    <div key={group}>
                      <p className="text-[10px] font-bold text-slate-500 tracking-wider uppercase mb-1.5">{group}</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {MODULE_OPTIONS.filter((m) => m.group === group).map((mod) => {
                          const checked = form.module_permissions.includes(mod.value);
                          return (
                            <label key={mod.value} className={`flex items-center gap-2 p-2 rounded-md text-xs cursor-pointer transition-colors border ${checked ? "border-[#8B1A1A] bg-red-50 text-slate-900" : "border-slate-200 bg-white hover:border-slate-300 text-slate-600"}`} data-testid={`create-perm-${mod.value}`}>
                              <input type="checkbox" checked={checked} onChange={() => toggleCreateModule(mod.value)} className="accent-[#8B1A1A] w-3.5 h-3.5" />
                              {mod.label}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={creating}
                className="w-full py-2.5 bg-[#8B1A1A] text-white rounded-lg font-medium hover:bg-[#6B1515] transition-colors disabled:opacity-50"
                data-testid="submit-create-user-btn"
              >
                {creating ? "Creating..." : "Create User"}
              </button>
            </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Mobile Login</th>
                <th>Linked Employee</th>
                <th>Permissions</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-slate-500">No users found</td></tr>
              ) : (
                users.map((u) => {
                  const linkedEmp = employees.find((e) => e.employee_id === u.linked_employee_id);
                  return (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#1A1A2E] flex items-center justify-center text-white text-xs font-semibold">
                          {u.name?.charAt(0) || "?"}
                        </div>
                        <span className="text-sm font-medium text-slate-900">{u.name}</span>
                      </div>
                    </td>
                    <td className="text-sm text-slate-600">{u.email}</td>
                    <td>
                      <Badge className={`text-xs ${ROLE_COLORS[u.role] || "bg-slate-100 text-slate-700"}`}>
                        {u.role?.replace("_", " ")}
                      </Badge>
                    </td>
                    <td>
                      {u.role === "SUPER_ADMIN" || u.mobile_login_enabled ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid={`mobile-yes-${u.user_id}`}>📱 Allowed</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-slate-500" data-testid={`mobile-no-${u.user_id}`}>Disabled</Badge>
                      )}
                    </td>
                    <td>
                      {linkedEmp ? (
                        <Badge className="bg-violet-100 text-violet-700 text-[10px]" data-testid={`linked-emp-${u.user_id}`}>
                          {linkedEmp.full_name}
                        </Badge>
                      ) : u.linked_employee_id ? (
                        <Badge variant="outline" className="text-[10px]">{u.linked_employee_id}</Badge>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1 max-w-[180px]">
                        {(u.module_permissions || []).length > 0 ? (
                          u.module_permissions.slice(0, 3).map((p) => (
                            <Badge key={p} variant="outline" className="text-[10px] px-1.5 py-0.5">{p.split(".").pop()}</Badge>
                          ))
                        ) : (
                          <span className="text-xs text-slate-400">{u.role === "SUPER_ADMIN" ? "Full Access" : "None"}</span>
                        )}
                        {(u.module_permissions || []).length > 3 && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">+{u.module_permissions.length - 3}</Badge>
                        )}
                      </div>
                    </td>
                    <td>
                      {u.is_active !== false ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">Active</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 text-xs">Inactive</Badge>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        {u.user_id !== currentUser?.user_id && (
                          <>
                            <button onClick={() => openEdit(u)} className="p-1.5 rounded-lg hover:bg-slate-50 text-slate-400 hover:text-slate-700 transition-colors" title="Edit User" data-testid={`edit-user-${u.user_id}`}>
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openPermissions(u)}
                              className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors"
                              title="Manage Permissions"
                              data-testid={`manage-permissions-${u.user_id}`}
                            >
                              <Settings2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeactivate(u.user_id)}
                              className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                              title="Deactivate user"
                              data-testid={`deactivate-user-${u.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Module Permissions Dialog */}
      <Dialog open={!!permUser} onOpenChange={(open) => { if (!open) setPermUser(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Sora']">Manage Permissions</DialogTitle>
            <DialogDescription>Assign specific module access to this user</DialogDescription>
          </DialogHeader>
          {permUser && (
            <div className="mt-2 space-y-4" data-testid="permissions-dialog">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-9 h-9 rounded-full bg-[#1A1A2E] flex items-center justify-center text-white text-xs font-semibold">
                  {permUser.name?.charAt(0) || "?"}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">{permUser.name}</p>
                  <p className="text-xs text-slate-500">{permUser.email} &middot; {permUser.role?.replace("_", " ")}</p>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700 mb-3">Module Access · {permModules.length} of {MODULE_OPTIONS.length} selected</p>
                <div className="max-h-[50vh] overflow-y-auto pr-1 space-y-3">
                  {MODULE_GROUPS.map((group) => (
                    <div key={group}>
                      <p className="text-[10px] font-bold text-slate-500 tracking-wider uppercase mb-1.5">{group}</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {MODULE_OPTIONS.filter((m) => m.group === group).map((mod) => {
                          const checked = permModules.includes(mod.value);
                          return (
                            <label
                              key={mod.value}
                              className={`flex items-center gap-2 p-2 rounded-md text-xs cursor-pointer transition-colors border ${checked ? "border-[#8B1A1A] bg-red-50 text-slate-900" : "border-slate-200 bg-white hover:border-slate-300 text-slate-600"}`}
                              data-testid={`perm-toggle-${mod.value}`}
                            >
                              <input type="checkbox" checked={checked} onChange={() => toggleModule(mod.value)} className="accent-[#8B1A1A] w-3.5 h-3.5" />
                              {mod.label}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setPermUser(null)}
                  className="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"
                  data-testid="cancel-permissions-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={savePermissions}
                  disabled={savingPerm}
                  className="flex-1 py-2.5 bg-[#8B1A1A] text-white rounded-lg text-sm font-medium hover:bg-[#6B1515] disabled:opacity-50"
                  data-testid="save-permissions-btn"
                >
                  {savingPerm ? "Saving..." : "Save Permissions"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User: {editUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Name</label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Role</label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Region</label>
              <Select value={editForm.region_id || "ALL"} onValueChange={(v) => setEditForm({ ...editForm, region_id: v === "ALL" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Select region" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Regions</SelectItem>
                  <SelectItem value="GJ">Gujarat</SelectItem>
                  <SelectItem value="RJ">Rajasthan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">New Password (leave blank to keep current)</label>
              <Input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Leave blank to keep current" data-testid="edit-user-password" />
            </div>
            <label className="flex items-center gap-2 p-2.5 bg-emerald-50 rounded-md border border-emerald-200 cursor-pointer">
              <input type="checkbox" checked={editForm.mobile_login_enabled} onChange={(e) => setEditForm({ ...editForm, mobile_login_enabled: e.target.checked })} className="accent-emerald-600 w-4 h-4" data-testid="edit-mobile-toggle" />
              <span className="text-xs text-slate-700"><span className="font-semibold">Allow mobile app login</span></span>
            </label>
            <div>
              <label className="text-sm font-medium text-slate-700">Linked Employee</label>
              <Select value={editForm.linked_employee_id || "NONE"} onValueChange={(v) => setEditForm({ ...editForm, linked_employee_id: v === "NONE" ? "" : v })}>
                <SelectTrigger data-testid="edit-link-employee"><SelectValue placeholder="— None —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">— None —</SelectItem>
                  {employees.filter((e) => !e.linked_user_id || e.linked_user_id === editUser?.user_id).map((e) => (
                    <SelectItem key={e.employee_id} value={e.employee_id}>{e.full_name} ({e.employee_code || e.employee_id})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editForm.role === "FACTORY_ADMIN" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700">Factory Section Access</label>
                  <button type="button" onClick={toggleAllFactorySections} className="text-xs text-[#8B1A1A] hover:underline" data-testid="toggle-all-factory-sections">
                    {editForm.factory_visible_sections.length === FACTORY_SECTIONS.length ? "Deselect All" : "Select All"}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1.5 max-h-[200px] overflow-y-auto border rounded-lg p-2 bg-slate-50">
                  {FACTORY_SECTIONS.map((s) => {
                    const checked = editForm.factory_visible_sections.includes(s.id);
                    return (
                      <label key={s.id} className={`flex items-center gap-2 p-2 rounded-md text-xs cursor-pointer transition-colors ${checked ? "bg-amber-50 border border-amber-200 text-slate-900" : "bg-white border border-slate-100 text-slate-500 hover:border-slate-200"}`} data-testid={`factory-section-${s.id}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleFactorySection(s.id)} className="accent-amber-500 w-3.5 h-3.5" />
                        {s.label}
                      </label>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-400 mt-1">{editForm.factory_visible_sections.length}/{FACTORY_SECTIONS.length} sections enabled</p>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditUser(null)} className="flex-1">Cancel</Button>
              <Button onClick={handleEditSave} className="flex-1 bg-[#8B1A1A] hover:bg-[#6B1515] text-white">Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPage;
